// Label and tooltip for the Days column of the Claim History table (SEQ-224).
//
// Pure so Deno can test it. Calendar days are counted on UTC date-only values,
// so a daylight saving change in the viewer's timezone cannot turn a whole-day
// gap into a fractional one.

export type PaymentDaysTone = "good" | "warn" | "bad";

export interface PaymentDaysLabel {
  label: string;
  tooltip: string;
  tone: PaymentDaysTone;
  // Paid rows use the small badge size, unpaid rows the slightly larger one.
  paid: boolean;
}

const MS_PER_DAY = 24 * 60 * 60 * 1000;

function dateOnlyUtc(value: string | null | undefined): number | null {
  if (!value) return null;
  const match = /^(\d{4})-(\d{2})-(\d{2})/.exec(value);
  if (!match) return null;
  return Date.UTC(Number(match[1]), Number(match[2]) - 1, Number(match[3]));
}

// Whole calendar days from `from` to `to` (positive when `to` is later).
function wholeDaysBetween(from: number, to: number): number {
  return Math.round((to - from) / MS_PER_DAY);
}

function plural(n: number): string {
  return `${n} day${n === 1 ? "" : "s"}`;
}

export function paymentDaysLabel(
  paymentDueDate: string | null | undefined,
  datePaid: string | null | undefined,
  fallbackDays: number | null | undefined,
  todayStr: string
): PaymentDaysLabel | null {
  const due = dateOnlyUtc(paymentDueDate);
  const paid = dateOnlyUtc(datePaid);

  if (paid !== null) {
    if (due === null) {
      return {
        label: "Paid",
        tooltip:
          "Payment date recorded. No due date is available for day-difference audit.",
        tone: "good",
        paid: true,
      };
    }
    const paidVsDue = wholeDaysBetween(paid, due);
    if (paidVsDue < 0) {
      const late = Math.abs(paidVsDue);
      return {
        label: `Paid ${late}d late`,
        tooltip: `Paid ${plural(late)} after due date.`,
        tone: "warn",
        paid: true,
      };
    }
    return {
      label: "Paid",
      tooltip:
        paidVsDue === 0
          ? "Paid on due date."
          : `Paid ${plural(paidVsDue)} before due date.`,
      tone: "good",
      paid: true,
    };
  }

  const today = dateOnlyUtc(todayStr);
  const days =
    due !== null && today !== null
      ? wholeDaysBetween(today, due)
      : (fallbackDays ?? null);
  if (days === null) return null;

  const abs = Math.abs(days);
  return {
    label:
      days < 0 ? `${abs}d overdue` : days === 0 ? "Due today" : `${days}d left`,
    tooltip:
      due !== null
        ? days < 0
          ? `Due date passed ${plural(abs)} ago.`
          : days === 0
            ? "Payment is due today."
            : `${plural(days)} until due date.`
        : "Using stored days value because this report has no due date.",
    tone: days < 0 ? "bad" : days <= 7 ? "warn" : "good",
    paid: false,
  };
}
